import React, { useState } from 'react';
import FurnitureNavbar from '../../../components/layout/Nav';
import './newproject.scss';
import withAuth from '../../auth/WithAuth';
import { serverUrlAuth } from '../../../serverUrl';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';

const NewProject = () => {
  const navigate = useNavigate();

  const [projectData, setProjectData] = useState({
    title: '',
    description: '',
    budget: '',
    location: '',
    deadline: '',
  });

  const [images, setImages] = useState([]); // actual File objects
  const [previewUrls, setPreviewUrls] = useState([]); // for image preview
  const [loading, setLoading] = useState(false);

  // Handle text input change
  const handleChange = (e) => {
    const { name, value } = e.target;
    setProjectData((prev) => ({ ...prev, [name]: value }));
  };

  // Handle image uploads
  const handleImageUpload = (e) => {
    const files = Array.from(e.target.files);
    const newPreviews = files.map((file) => URL.createObjectURL(file));

    setImages((prev) => [...prev, ...files]);
    setPreviewUrls((prev) => [...prev, ...newPreviews]);
  };

  // Remove a selected image
  const handleRemoveImage = (index) => {
    setImages((prev) => prev.filter((_, i) => i !== index));
    setPreviewUrls((prev) => prev.filter((_, i) => i !== index));
  };

  // Submit form
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const formData = new FormData();
      formData.append('title', projectData.title);
      formData.append('description', projectData.description);
      formData.append('budget', projectData.budget);
      formData.append('location', projectData.location);
      formData.append('deadline', projectData.deadline);

      images.forEach((file) => formData.append('uploaded_images', file));

      await serverUrlAuth.post('projects/create-project/', formData);

      toast.success('🎉 Project created successfully and artisans assigned!');
      navigate('/projects');
    } catch (error) {
      console.error('Upload error:', error);
      toast.error('❌ Failed to create project. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <FurnitureNavbar />
      <div className="new-project-page">
        {/* HERO SECTION */}
        <section className="project-hero">
          <div className="overlay">
            <div className="hero-content">
              <h1>Bring Your Vision to Life 🎨</h1>
              <p>
                Describe your dream piece, set your budget, and connect with
                artisans who’ll make it real.
              </p>
            </div>
          </div>
        </section>

        {/* MAIN FORM SECTION */}
        <div className="project-workspace container">
          {/* Left: Form */}
          <form className="project-form" onSubmit={handleSubmit}>
            <h2>🪚 Project Details</h2>

            <div className="form-group">
              <label>Project Title</label>
              <input
                type="text"
                name="title"
                placeholder="e.g. Custom Oak Dining Table"
                value={projectData.title}
                onChange={handleChange}
                required
              />
            </div>

            <div className="form-group">
              <label>
                Description{' '}
                <span className="description-span">
                  (Give the dimensions,material, material colour, colour code if
                  possible e.t.c. be as specific as possible)
                </span>
              </label>

              <textarea
                name="description"
                placeholder="Describe your project idea, preferred materials, dimensions, etc."
                value={projectData.description}
                onChange={handleChange}
                required
              />
            </div>

            <div className="form-group">
              <label>Estimated Budget (KSh)</label>
              <input
                type="number"
                name="budget"
                placeholder="e.g. 25,000"
                value={projectData.budget}
                onChange={handleChange}
                required
              />
            </div>

            <div className="form-group">
              <label>Location</label>
              <input
                type="text"
                name="location"
                placeholder="e.g. Nairobi, Kenya"
                value={projectData.location}
                onChange={handleChange}
                required
              />
            </div>
            <div className="form-group deadline-field premium">
              <label className="form-label">Preferred Deadline</label>

              <div>
                <div className="date-input-wrapper">
                  <input
                    type="date"
                    min={new Date().toISOString().split('T')[0]}
                    name="deadline"
                    value={projectData.deadline}
                    onChange={handleChange}
                    className="date-input"
                    required
                  />
                </div>
              </div>

              {projectData.deadline && (
                <div className="deadline-preview">
                  <div className="preview-header">
                    <span className="preview-icon">📅</span>
                    <span className="preview-title">
                      Selected Proposed Completion Date
                    </span>
                  </div>
                  <div className="selected-date">
                    {new Date(projectData.deadline).toLocaleDateString(
                      'en-US',
                      {
                        weekday: 'long',
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric',
                      },
                    )}
                  </div>
                  <p className="days-from-now">
                    {(() => {
                      const today = new Date();
                      const selectedDate = new Date(projectData.deadline);
                      // Reset times to avoid partial day issues
                      today.setHours(0, 0, 0, 0);
                      selectedDate.setHours(0, 0, 0, 0);
                      const diffTime = selectedDate - today;
                      const diffDays = Math.round(
                        diffTime / (1000 * 60 * 60 * 24),
                      );
                      return `This is ${diffDays} day${
                        diffDays !== 1 ? 's' : ''
                      } from now.`;
                    })()}
                  </p>
                </div>
              )}
            </div>

            <div className="form-group">
              <label>Reference Images</label>
              <input
                type="file"
                multiple
                accept="image/*"
                onChange={handleImageUpload}
              />
            </div>

            <button type="submit" className="submit-btn" disabled={loading}>
              {loading ? 'Uploading...' : '🚀 Post Project'}
            </button>
          </form>

          {/* Right: Preview Board */}
          <div className="preview-board">
            <h2>📸 Inspiration Board</h2>
            <p>
              Upload photos or sketches of your desired style, and we’ll match
              you with artisans who can bring it to life.
            </p>

            <div className="image-grid">
              {previewUrls.length > 0 ? (
                previewUrls.map((url, index) => (
                  <div key={index} className="image-card">
                    <img src={url} alt="Preview" />
                    <button
                      type="button"
                      className="remove-btn"
                      onClick={() => handleRemoveImage(index)}
                    >
                      ✖
                    </button>
                  </div>
                ))
              ) : (
                <div className="placeholder">
                  <span>🖼 No images uploaded yet</span>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default withAuth(NewProject, 'client');
